import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AdminDeposit,
    useReleaseDepositMutation,
} from '../../../api/deposit/depositAdminApiSlice';
import { getCurrentLang } from '../../../i18n/i18n';
import { formatCurrency } from '../../../utils';
import DepositModalShell, { ReasonField } from './DepositModalShell';
import { useDepositAction } from './useDepositAction';

interface Props {
    deposit: AdminDeposit;
    onClose: () => void;
}

// Release the deposit hold (cancel the authorization) — the money was only
// reserved on the customer's card, so the copy says "released", never
// "refunded". The reason is optional free text; the backend accepts and
// discards it in v1 (spec §4), so the contract stays stable if a release
// reason column is added later.
const ReleaseDepositModal = ({ deposit, onClose }: Props) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [releaseDeposit, { isLoading }] = useReleaseDepositMutation();
    const { formError, clearFormError, submit } = useDepositAction('ReleaseDepositModal', onClose);

    const handleRelease = () =>
        submit(() =>
            releaseDeposit({
                orderId: deposit.orderId,
                reason: reason.trim().length === 0 ? null : reason.trim(),
            }).unwrap(),
        );

    return (
        <DepositModalShell
            title={t('deposits.release.title')}
            description={t('deposits.release.description', {
                orderNumber: deposit.orderNumber,
                amount: formatCurrency(deposit.depositAmount, getCurrentLang()),
            })}
            confirmLabel={t('deposits.release.confirm')}
            loading={isLoading}
            onConfirm={handleRelease}
            onClose={onClose}
        >
            <ReasonField
                id={'release-reason'}
                label={t('deposits.release.reason_label')}
                value={reason}
                onChange={(value) => {
                    setReason(value);
                    clearFormError();
                }}
            />
            {formError && <p className={'text-sm text-red-500'}>{formError}</p>}
        </DepositModalShell>
    );
};

export default ReleaseDepositModal;
